import type { ButtonSize } from '@timelyapp/react';
import { Button, CombinedButton, Text } from '@timelyapp/react';
import classNames from 'classnames';
import DatePickerDropdown from 'components/datepicker/DatePickerDropdown';
import { getDatePickerTargetText } from 'components/datepicker/DatePickerTarget';
import type { DatePickerMode, RelativeDateRange } from 'components/datepicker/types';
import { getSpanForRelativeDateRange } from 'components/datepicker/utils';
import { DateTime } from 'luxon';
import React, { Component } from 'react';
import FontAwesome from 'react-fontawesome';
import type { ReportQueryExtended } from 'redux/modules/superreports.types';
import type { Query } from 'redux/modules/users-page/users-list/usersList.types';
import type { Account } from 'types/account';
import type { DateTypeFilter, DateTypePeriodNames } from 'types/dates';
import type { MoveDateOptions } from 'utils/date-helper';
import { isSameDay, isSameMonth, isSameYear, moveDateBackOrForward } from 'utils/date-helper';
import styles from './ReportQueryDateOptions.module.scss';

const defaultProps = {
  disabledPeriods: ['unrestricted'] as Array<DateTypePeriodNames>,
  enabledModes: ['day', 'month', 'year'],
  enableRangeMode: true,
  showRelativeDateRanges: false,
};

interface Props {
  account: Account;
  className?: string;
  enabledModes?: Array<DatePickerMode>;
  enableRangeMode?: boolean;
  disabledPeriods?: Array<DateTypePeriodNames>;
  query: Query | ReportQueryExtended;
  readOnly?: boolean;
  onSetQuery: (query: any) => unknown;
  size?: ButtonSize;
  showRelativeDateRanges?: boolean;
}

class ReportQueryDateOptions extends Component<Props> {
  static defaultProps = defaultProps;

  handleSetQuery = (query: DateTypeFilter) => {
    this.props.onSetQuery(query);
  };

  handleDateNavigation = (direction = 'back' as MoveDateOptions['direction']) => {
    // the old version of the DatePicker had a "custom" mode
    // which is no longer supported but still used by reports
    const timespan = this.props.query.span === 'custom' ? 'day' : this.props.query.span;

    const query = moveDateBackOrForward(
      { from: this.props.query.from!, to: this.props.query.to! },
      {
        direction,
        timespan: timespan as MoveDateOptions['timespan'],
        startOfWeek: this.props.account.start_of_week,
      }
    );

    this.handleSetQuery(query);
  };

  handleChangeToDateRange = (date: string, dateRange: Partial<Props['query']>) => {
    if (dateRange.from === this.props.query.from && dateRange.to === this.props.query.to) return;
    this.handleSetQuery(dateRange);
  };

  handleGoForward = () => this.handleDateNavigation('forward' as const);

  handleGoBack = () => this.handleDateNavigation('back');

  handleChangeFromDate = (day: string) => this.handleSetQuery({ from: day });

  handleChangeToDate = (day: string) => this.handleSetQuery({ to: day });

  handleRelativeDateRangeSelect = (
    _: React.SyntheticEvent<HTMLElement>,
    relativeDateRange: RelativeDateRange
  ) => {
    this.props.onSetQuery({
      relativeDateRange,
      span: getSpanForRelativeDateRange(relativeDateRange),
    });
  };

  private static getFormattingOptions = (from: string, to?: string | null) => {
    const now = DateTime.now().toISODate();
    const isFromThisYear = isSameYear(from, now);
    const isToThisYear = to ? isSameYear(to, now) : false;
    const isSingleDay = to ? isSameDay(from, to) : false;
    const customDateFormat = isFromThisYear && isToThisYear ? 'MMMM Do' : 'MMMM Do, YYYY';

    return {
      from,
      to,
      isFromThisYear,
      isToThisYear,
      isSingleDay,
      customDateFormat,
    };
  };

  private renderReadOnly = ({
    from,
    to,
    isFromThisYear,
    isToThisYear,
    isSingleDay,
  }: ReturnType<typeof ReportQueryDateOptions.getFormattingOptions>) => {
    const { className } = this.props;
    return (
      <div className={classNames(styles.container, styles.readOnly, className)}>
        {isSingleDay ? (
          <strong>
            {DateTime.fromISO(from).toLocaleString({
              ...DateTime.DATE_MED,
              weekday: 'long',
              year: !isFromThisYear ? 'numeric' : undefined,
            })}
          </strong>
        ) : (
          <>
            From&nbsp;
            <strong>
              {DateTime.fromISO(from).toLocaleString({
                ...DateTime.DATE_MED,
                weekday: 'long',
                year: !isFromThisYear ? 'numeric' : undefined,
              })}
            </strong>
            &nbsp;to&nbsp;
            <strong>
              {DateTime.fromISO(to!).toLocaleString({
                ...DateTime.DATE_MED,
                weekday: 'long',
                year: !isToThisYear ? 'numeric' : undefined,
              })}
            </strong>
          </>
        )}
      </div>
    );
  };

  handleCustomRangeEnabledChange = (customRangeEnabled: boolean) =>
    this.props.onSetQuery({ customRangeEnabled });

  render() {
    const { query, className, readOnly, size = 'small', showRelativeDateRanges } = this.props;
    if (!query.from) return null;
    const isRelativeRange = 'relativeDateRange' in query && query.relativeDateRange !== undefined;

    const formattingOptions = ReportQueryDateOptions.getFormattingOptions(query.from, query.to);
    if (readOnly) return this.renderReadOnly(formattingOptions);

    return (
      <div className={classNames(styles.container, className)}>
        <CombinedButton>
          {!isRelativeRange && (
            <Button size={size} onClick={this.handleGoBack}>
              <FontAwesome name="chevron-left" />
            </Button>
          )}

          <DatePickerDropdown
            initialMode={query.span as DatePickerMode}
            startOfWeek={this.props.account.start_of_week}
            baseDate={query.from}
            dateRange={{ from: query.from, to: query.to }}
            onDateChange={this.handleChangeToDateRange}
            enabledModes={this.props.enabledModes}
            onModeChanged={span => this.props.onSetQuery({ span })}
            onCustomRangeEnabledChange={this.handleCustomRangeEnabledChange}
            inButtonGroup={!isRelativeRange}
            enableRangeMode={this.props.enableRangeMode}
            startNewRangeOnMount={
              !isRelativeRange && query.span === 'month' && isSameMonth(query.from, query.to!)
            }
            position="bottom-start"
            fixed
            size={size}
            showRelativeDateRanges={showRelativeDateRanges}
            relativeDateRange={isRelativeRange ? query.relativeDateRange : undefined}
            onRelativeDateRangeSelect={this.handleRelativeDateRangeSelect}
          />

          {!isRelativeRange && (
            <Button size={size} onClick={this.handleGoForward}>
              <FontAwesome name="chevron-right" />
            </Button>
          )}
        </CombinedButton>

        {isRelativeRange && (
          <Text size="small" color="subtle" className={styles.dateRangeExplanation}>
            Dynamic date range, currently&nbsp;
            {getDatePickerTargetText(
              'week',
              query.from,
              { autoPostfixYear: false, startOfWeek: this.props.account.start_of_week },
              { from: query.from, to: query.to }
            )}
          </Text>
        )}
      </div>
    );
  }
}

export default ReportQueryDateOptions;
